import { useNavigation } from '@react-navigation/native';
import { Meta, SpeechBubble, theme } from 'aimo-ui';
import VehicleCameraRecognitionSelector from 'components/account/vehicle/VehicleCameraRecognitionSelector';
import VehicleChargerPlugTypeSelector from 'components/account/vehicle/VehicleChargerPlugTypeSelector';
import VehicleCountrySelector from 'components/account/vehicle/VehicleCountrySelector';
import VehicleLicensePlateInput from 'components/account/vehicle/VehicleLicensePlateInput';
import VehiclePermitSelector from 'components/account/vehicle/VehiclePermitSelector';
import VehicleTypeSelector from 'components/account/vehicle/VehicleTypeSelector';
import SvgImage, { SvgName } from 'components/svgImage/SvgImage';
import AccountContext from 'context/AccountContext';
import FeatureFlagsContext from 'context/FeatureFlagsContext';
import useGetAvailableAnprPaymentOption from 'hooks/useGetAvailableAnprPaymentOption';
import React, { useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useVehicleStore } from 'stores/vehicleStore';
import { StyledWrapper } from 'styles/ContainerStyles';
import { VehicleCountryCode } from 'types/generatedSchemaTypes';
import { RootNavProps } from 'types/navigation';
import { showFeatureFlag } from 'utils/featureFlagUtils';
import {
  isLicensePlateFormatValid,
  isVehicleLowEmission,
} from 'utils/vehicleUtils';
import VehicleLowEmissionInfo from './VehicleLowEmissionInfo';

type VehicleFormDetailsProps = {
  showLicensePlateError?: boolean;
  permitsAmount?: number;
  isOnboarding?: boolean;
};

type SvgSize = keyof typeof theme.iconSizes;

type CustomSpeechBubbleProps = {
  config: {
    name: SvgName;
    size: SvgSize;
    iconColor: any;
    color: any;
    message: string;
  };
};

const CustomSpeechBubble = ({ config }: CustomSpeechBubbleProps) => {
  const { name, size, iconColor, color, message } = config;
  return (
    <SpeechBubble
      leftItem={<SvgImage size={size} name={name} color={iconColor} />}
      color={color}
      centerItem={<Meta>{message}</Meta>}
    />
  );
};

const ConditionalSpeechBubble = ({
  cameraRecognitionEnabled,
}: {
  cameraRecognitionEnabled: boolean;
}) => {
  const { t } = useTranslation();
  const anprPaymentOption = useGetAvailableAnprPaymentOption();

  const speechBubbleConfigs = {
    CAMERA_DISABLED: {
      name: 'Camera-Recognition-Illustration' as SvgName,
      size: 'xxl' as SvgSize,
      iconColor: undefined,
      color: theme.colors.lightBg.green,
      message: t('vehicle.cameraRecognition.tutorial.notEnabled'),
    },
    COMPANY_POOL: {
      name: 'Payment-Card' as SvgName,
      size: 'xxl' as SvgSize,
      iconColor: undefined,
      color: theme.colors.lightBg.yellow,
      message: t('vehicle.cameraRecognition.tutorial.limitedLocation'),
    },
    NO_OPTIONS: {
      name: 'Info-Outlined' as SvgName,
      size: 'm' as SvgSize,
      iconColor: theme.colors.aimoUiRed,
      color: theme.colors.lightBg.yellow,
      message: t('vehicle.cameraRecognition.tutorial.noPayment'),
    },
    TOKENIZED_PAYMENT_OPTION: null,
    CORPORATE_ACCOUNT: null,
  };

  if (!cameraRecognitionEnabled) {
    const config = speechBubbleConfigs.CAMERA_DISABLED;
    return <CustomSpeechBubble config={config} />;
  }

  const config = speechBubbleConfigs[anprPaymentOption];

  if (!config) {
    return null;
  }

  return <CustomSpeechBubble config={config} />;
};

const VehicleFormDetails = ({
  showLicensePlateError,
  permitsAmount = 0,
  isOnboarding = false,
}: VehicleFormDetailsProps) => {
  const { navigate } = useNavigation<RootNavProps>();
  const [showInvalidFormatInfo, setShowInvalidFormatInfo] =
    React.useState<boolean>(false);

  const {
    flags: {
      anpr,
      vehicle_registration_country,
      vehicle_registration_country_edit,
      vehicle_type,
    },
  } = useContext(FeatureFlagsContext);
  const {
    customer: { licensePlateBasedPermits },
  } = useContext(AccountContext);

  const { vehicleData, setVehicleData } = useVehicleStore((state) => state);
  const { appSettings, cameraRecognition, licensePlate, id } =
    vehicleData ?? {};
  const { type, evPlugType } = appSettings ?? {};
  const { enabled } = cameraRecognition ?? {};
  const { plateText, countryCode } = licensePlate ?? {};
  const showCameraRecognition = showFeatureFlag(anpr);
  const showRegistrationCountry = showFeatureFlag(vehicle_registration_country);
  const showCountryEdit = showFeatureFlag(vehicle_registration_country_edit);
  const showVehicleType = showFeatureFlag(vehicle_type);

  const licensePlateCheck = useCallback(
    (licensePlateTrimmed: string) => {
      const isValidFormat = isLicensePlateFormatValid(
        licensePlateTrimmed,
        countryCode ?? undefined
      );
      setShowInvalidFormatInfo(!isValidFormat);
    },
    [countryCode]
  );

  const vehicleCountryVisible = !id ? showRegistrationCountry : showCountryEdit;

  return (
    <>
      <StyledWrapper mb={20}>
        <VehicleLicensePlateInput
          licensePlate={plateText}
          setLicensePlate={setVehicleData}
          showError={showLicensePlateError}
          disabled={!!vehicleData?.id && plateText !== ''}
          onBlur={licensePlateCheck}
          showInvalidFormatInfo={showInvalidFormatInfo}
        />
      </StyledWrapper>
      {showVehicleType && !!vehicleData?.id && (
        <StyledWrapper mb={20}>
          <VehicleTypeSelector
            selectedType={type}
            setSelectedType={setVehicleData}
          />
        </StyledWrapper>
      )}
      {vehicleCountryVisible && (
        <StyledWrapper mb={20}>
          <VehicleCountrySelector
            selectedCountry={countryCode as VehicleCountryCode}
            setSelectedCountry={setVehicleData}
            isVehicleNew={!id}
          />
        </StyledWrapper>
      )}
      {type === 'ELECTRIC' && (
        <StyledWrapper mb={20}>
          <VehicleChargerPlugTypeSelector
            selectedChargerPlugType={evPlugType}
            setSelectedChargerPlugType={setVehicleData}
          />
        </StyledWrapper>
      )}
      {vehicleData?.id &&
        isVehicleLowEmission(licensePlateBasedPermits, vehicleData) && (
          <StyledWrapper mb={20}>
            <VehicleLowEmissionInfo />
          </StyledWrapper>
        )}
      {showCameraRecognition && (
        <>
          <StyledWrapper mb={enabled ? 20 : 5}>
            <VehicleCameraRecognitionSelector
              selectedCameraRecognition={enabled ?? false}
              isOnboarding={isOnboarding}
            />
          </StyledWrapper>

          <ConditionalSpeechBubble
            cameraRecognitionEnabled={enabled ?? false}
          />
        </>
      )}
      {permitsAmount > 0 && (
        <StyledWrapper mt={20} mb={20}>
          <VehiclePermitSelector
            selectedPermit={permitsAmount}
            onPress={() =>
              navigate('permits', {
                licensePlate: vehicleData?.licensePlate?.plateText,
              })
            }
          />
        </StyledWrapper>
      )}
    </>
  );
};

export default VehicleFormDetails;
